import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Receipt as InvoiceIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { requestService } from '../../services/requestService';
import { serviceProviderPortalService } from '../../services/serviceProviderPortalService';
import AddPaymentModal from '../../components/AddPaymentModal';
import authService from '../../services/auth.service';

const ServiceProviderRequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [request, setRequest] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  useEffect(() => {
    // Check if user is service provider
    const currentUser = authService.getCurrentUser();
    if (!currentUser) {
      navigate('/service-provider/login');
      return;
    }

    const roleType = currentUser?.role?.type || currentUser?.role?.attributes?.type;
    if (roleType !== 'service-provider') {
      authService.logout();
      navigate('/service-provider/login');
      return;
    }

    if (id) {
      loadRequest();
    }
  }, [id]);

  const loadRequest = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await requestService.getById(Number(id));
      setRequest(response.data);
    } catch (err: any) {
      console.error('Error loading request:', err);
      setError('Failed to load request details.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'assigned': return 'info';
      case 'active': return 'primary';
      case 'invoice_sent': return 'secondary';
      case 'payment_received': return 'success';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: any = {
      'pending': 'Pending',
      'assigned': 'Assigned',
      'active': 'Active',
      'invoice_sent': 'Invoice Sent',
      'payment_received': 'Payment Received',
      'completed': 'Completed',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const handleAcceptRequest = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError('');
      await serviceProviderPortalService.acceptRequest(Number(id));
      await loadRequest();
    } catch (err: any) {
      console.error('Error accepting request:', err);
      setError(err.response?.data?.error?.message || 'Failed to accept request');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequest = async () => {
    if (!id) return;
    
    const reason = window.prompt('Please provide a reason for rejecting this request:');
    if (!reason) return;
    
    try {
      setLoading(true);
      setError('');
      await serviceProviderPortalService.rejectRequest(Number(id), reason);
      navigate('/service-provider/dashboard');
    } catch (err: any) {
      console.error('Error rejecting request:', err);
      setError(err.response?.data?.error?.message || 'Failed to reject request');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!id) return;
    
    const confirmed = window.confirm('Confirm that payment has been received for this request?');
    if (!confirmed) return;
    
    try {
      setLoading(true);
      setError('');
      await serviceProviderPortalService.confirmPayment(Number(id));
      await loadRequest();
    } catch (err: any) {
      console.error('Error confirming payment:', err);
      setError(err.response?.data?.error?.message || 'Failed to confirm payment');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!request) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="error">Request not found.</Alert>
      </Container>
    );
  }

  const attrs = request?.attributes || request;
  const customer = attrs.customer?.data?.attributes || attrs.customer;
  const insuranceClient = attrs.insuranceClient?.data?.attributes || attrs.insuranceClient;
  const invoice = attrs.invoice?.data?.attributes || attrs.invoice;
  const serviceProvider = attrs.serviceProvider?.data?.attributes || attrs.serviceProvider;

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: '#F56A2C', color: 'white' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Request Details
            </Typography>
            <Typography variant="body2">
              Request #{attrs.requestNumber || request.id}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/service-provider/dashboard')}
            sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' } }}
          >
            Back to Dashboard
          </Button>
        </Stack>
      </Paper>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Request Information */}
          <Box sx={{ flex: 1 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Request Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip 
                    label={getStatusLabel(attrs.status || request.status)} 
                    color={getStatusColor(attrs.status || request.status) as any}
                    size="small"
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                  <Typography variant="body1">{attrs.description || request.description}</Typography>
                </Box>

                {attrs.serviceType && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Service Type</Typography>
                    <Typography variant="body1">{attrs.serviceType}</Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Scheduled Date</Typography>
                  <Typography variant="body1">{formatDate(attrs.scheduledDate || request.scheduledDate)}</Typography>
                </Box>

                {attrs.notes && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                    <Typography variant="body1">{attrs.notes}</Typography>
                  </Box>
                )}
              </Stack>
            </Paper>

            {/* Customer Information */}
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Customer Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                {insuranceClient && (
                  <>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Client Name</Typography>
                      <Typography variant="body1">{insuranceClient.customerName || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">VIN Number</Typography>
                      <Typography variant="body1">{insuranceClient.vinNumber || 'N/A'}</Typography>
                    </Box>
                    {insuranceClient.phoneNumber && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                        <Typography variant="body1">{insuranceClient.phoneNumber}</Typography>
                      </Box>
                    )}
                    {insuranceClient.email && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                        <Typography variant="body1">{insuranceClient.email}</Typography>
                      </Box>
                    )}
                    {attrs.locationText && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Location</Typography>
                        <Typography variant="body1">{attrs.locationText}</Typography>
                      </Box>
                    )}
                  </>
                )}
              </Stack>
            </Paper>
          </Box>

          {/* Actions Sidebar */}
          <Box sx={{ width: { xs: '100%', md: '350px' } }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Actions
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Stack spacing={2}>
                {/* Accept/Reject Request */}
                {(attrs.status === 'assigned' || request.status === 'assigned') && (
                  <>
                    <Button
                      variant="contained"
                      fullWidth
                      color="success"
                      onClick={handleAcceptRequest}
                      disabled={loading}
                      sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}
                    >
                      Accept Request
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      color="error"
                      onClick={handleRejectRequest}
                      disabled={loading}
                    >
                      Reject Request
                    </Button>
                  </>
                )}

                {/* Create Invoice */}
                {!invoice && (attrs.status === 'active' || request.status === 'active') && (
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<InvoiceIcon />}
                    onClick={() => navigate(`/service-provider/invoice/${request.id}`)}
                    sx={{ backgroundColor: '#F56A2C', '&:hover': { backgroundColor: '#D95C26' } }}
                  >
                    Create Invoice
                  </Button>
                )}

                {/* Confirm Payment */}
                {invoice && (attrs.status === 'invoice_sent' || request.status === 'invoice_sent') && (
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<PaymentIcon />}
                    onClick={handleConfirmPayment}
                    disabled={loading}
                    color="success"
                    sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}
                  >
                    Confirm Payment Received
                  </Button>
                )}

                {/* Add Payment (Alternative) */}
                {invoice && (attrs.status === 'invoice_sent' || request.status === 'invoice_sent') && (
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<PaymentIcon />}
                    onClick={() => setPaymentModalOpen(true)}
                    color="primary"
                  >
                    Add Payment Record
                  </Button>
                )}
              </Stack>

              {/* Invoice Info */}
              {invoice && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Invoice Details
                  </Typography>
                  <Stack spacing={1}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Invoice Number</Typography>
                      <Typography variant="body2">{invoice.invoiceNumber}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Total Amount</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        OMR {invoice.total?.toFixed(3) || '0.000'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Balance Due</Typography>
                      <Typography variant="body2" fontWeight="bold" color="error.main">
                        OMR {invoice.balanceDue?.toFixed(3) || '0.000'}
                      </Typography>
                    </Box>
                  </Stack>
                </>
              )}
            </Paper>
          </Box>
        </Box>

        {/* Add Payment Modal */}
        {request && (
          <AddPaymentModal
            open={paymentModalOpen}
            onClose={() => setPaymentModalOpen(false)}
            request={request}
            onSuccess={() => {
              loadRequest();
              setPaymentModalOpen(false);
            }}
          />
        )}
      </Container>
    </Box>
  );
};

export default ServiceProviderRequestDetail;

